import { useCallback, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { emptyAgentSessionListSnapshot } from './types';
import { useWorkspaceStore } from './workspaceStore';

const EMPTY_AGENT_SESSION_LIST = emptyAgentSessionListSnapshot();

type AgentSessionListKey = 'recent' | 'all';

export const useWorkspaceAgentSessionList = (
  workspaceId: string,
  key: AgentSessionListKey,
  options?: {
    limit?: number;
    enabled?: boolean;
  }
): {
  items: ReturnType<
    typeof useWorkspaceStore.getState
  >['agentSessionsByWorkspace'][string]['recent']['items'];
  loading: boolean;
  loadingMore: boolean;
  loadMoreError: boolean;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => void;
} => {
  const enabled = options?.enabled ?? true;
  const snapshot = useWorkspaceStore(
    useShallow(state => {
      const bucket = state.agentSessionsByWorkspace[workspaceId];
      return bucket?.[key] ?? EMPTY_AGENT_SESSION_LIST;
    })
  );
  const fetchRecentAgentSessions = useWorkspaceStore(
    state => state.fetchRecentAgentSessions
  );
  const fetchAllAgentSessions = useWorkspaceStore(
    state => state.fetchAllAgentSessions
  );
  const loadMoreAllAgentSessions = useWorkspaceStore(
    state => state.loadMoreAllAgentSessions
  );

  useEffect(() => {
    if (!enabled || !workspaceId) return;
    if (snapshot.loaded || snapshot.loading) return;
    if (key === 'recent') {
      void fetchRecentAgentSessions(workspaceId, options?.limit);
      return;
    }
    void fetchAllAgentSessions(workspaceId, options?.limit);
  }, [
    enabled,
    fetchAllAgentSessions,
    fetchRecentAgentSessions,
    key,
    options?.limit,
    snapshot.loaded,
    snapshot.loading,
    workspaceId,
  ]);

  const refresh = useCallback((): void => {
    if (key === 'recent') {
      void fetchRecentAgentSessions(workspaceId, options?.limit);
      return;
    }
    void fetchAllAgentSessions(workspaceId, options?.limit);
  }, [
    fetchAllAgentSessions,
    fetchRecentAgentSessions,
    key,
    options?.limit,
    workspaceId,
  ]);

  const loadMore = useCallback(async (): Promise<void> => {
    if (key !== 'all') return;
    await loadMoreAllAgentSessions(workspaceId);
  }, [key, loadMoreAllAgentSessions, workspaceId]);

  return {
    items: snapshot.items,
    loading: snapshot.loading,
    loadingMore: snapshot.loadingMore,
    loadMoreError: snapshot.loadMoreError,
    hasMore: snapshot.nextCursor !== null,
    loadMore,
    refresh,
  };
};
